/**
 * OPUS-913 — Access lock manager (region / IP locking, super-admin only).
 *
 * Reusable across renderer / scene / user / organisation. Backend gate +
 * enforcement live in @ae/auth (REGIONLOCK_MODE, default shadow). Defaults are
 * OPEN: a lock only bites when `enabled === true` AND a list is non-empty.
 * Org locks are a fallback default; a resource's own lock overrides.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, CircularProgress,
} from "@mui/material";
import { Icon as OpusIcon, Button as OpusButton, Badge as OpusBadge } from "../common/opus";
import { useAlert } from "../../contexts/AlertContext";
import apiService from "../../services/api.service";

const API = {
  renderer: { get: (id) => apiService.getRendererAccessLock(id), set: (id, l) => apiService.setRendererAccessLock(id, l) },
  scene: { get: (id) => apiService.getSceneAccessLock(id), set: (id, l) => apiService.setSceneAccessLock(id, l) },
  user: { get: (id) => apiService.getUserAccessLock(id), set: (id, l) => apiService.setUserAccessLock(id, l) },
  org: { get: (id) => apiService.getOrgAccessLock(id), set: (id, l) => apiService.setOrgAccessLock(id, l) },
};

const IP_RE = /^[0-9a-fA-F:.]+(\/\d{1,3})?$/;        // IPv4/IPv6 + optional CIDR
const ISO2_RE = /^[A-Za-z]{2}$/;

function Chips({ items, onRemove, tone = "neutral" }) {
  if (!items.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
      {items.map((v) => (
        <Box key={v} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, borderRadius: "999px", background: "var(--surface-3)", border: "1px solid var(--border)", fontSize: 12.5, fontFamily: "var(--font-mono)", color: "var(--text)" }}>
          {v}
          <Box component="span" onClick={() => onRemove(v)} sx={{ display: "inline-flex", cursor: "pointer", color: "var(--text-dim)", "&:hover": { color: "var(--coral)" } }}>
            <OpusIcon name="x" size={12} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function AccessLockModal({ open, onClose, type, id, name, onSaved }) {
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [ips, setIps] = useState([]);
  const [countries, setCountries] = useState([]);
  const [ipInput, setIpInput] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [updatedBy, setUpdatedBy] = useState(null);

  const svc = API[type];

  const load = useCallback(async () => {
    if (!open || !svc || !id) return;
    setLoading(true);
    try {
      const resp = await svc.get(id);
      const lock = resp?.data?.access_lock || resp?.access_lock || resp?.data || resp || {};
      setEnabled(!!lock.enabled);
      setIps(Array.isArray(lock.ip_allowlist) ? lock.ip_allowlist : []);
      setCountries(Array.isArray(lock.allowed_countries) ? lock.allowed_countries : []);
      setUpdatedBy(lock.updated_by || null);
    } catch (e) {
      // fail-open: treat as no lock
      setEnabled(false); setIps([]); setCountries([]); setUpdatedBy(null);
    } finally {
      setLoading(false);
    }
  }, [open, svc, id]);

  useEffect(() => { load(); }, [load]);

  const addIp = () => {
    const v = ipInput.trim();
    if (!v) return;
    if (!IP_RE.test(v)) { alert.error("Enter a valid IP or CIDR (e.g. 203.0.113.4 or 10.0.0.0/24)"); return; }
    if (!ips.includes(v)) setIps([...ips, v]);
    setIpInput("");
  };
  const addCountry = () => {
    const v = countryInput.trim().toUpperCase();
    if (!v) return;
    if (!ISO2_RE.test(v)) { alert.error("Enter a 2-letter ISO country code (e.g. GB, US)"); return; }
    if (!countries.includes(v)) setCountries([...countries, v]);
    setCountryInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await svc.set(id, { enabled, ip_allowlist: ips, allowed_countries: countries });
      alert.success("Access lock updated");
      onSaved && onSaved({ enabled, ip_allowlist: ips, allowed_countries: countries });
      onClose && onClose();
    } catch (e) {
      alert.error(e?.message || "Failed to update access lock");
    } finally {
      setSaving(false);
    }
  };

  const openButEmpty = enabled && ips.length === 0 && countries.length === 0;
  const TYPE_LABEL = { renderer: "renderer", scene: "scene", user: "user", org: "organisation" }[type] || type;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: "var(--elevated)", border: "1px solid var(--border-strong)", borderRadius: "var(--r-lg)", color: "var(--text)", boxShadow: "var(--shadow-lg)" } }}
      sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.6)" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.25, fontWeight: 700 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "var(--r-md)", background: "rgba(252,193,68,0.14)", color: "var(--amber)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <OpusIcon name="lock" size={18} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>Access lock</Typography>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)" }}>{TYPE_LABEL} · {name || id}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "var(--border)" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={26} sx={{ color: "var(--green)" }} /></Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Enabled */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Restrict access</Typography>
                <Typography sx={{ fontSize: 12.5, color: "var(--text-dim)" }}>When off, this {TYPE_LABEL} is open to everyone (default).</Typography>
              </Box>
              <Box component="button" type="button" role="switch" aria-checked={enabled} onClick={() => setEnabled((v) => !v)}
                sx={{ flexShrink: 0, width: 38, height: 22, borderRadius: 999, border: "none", p: "2px", cursor: "pointer", background: enabled ? "var(--coral)" : "var(--surface-3)", transition: "background .16s" }}>
                <Box sx={{ width: 18, height: 18, borderRadius: 999, background: "#fff", transform: enabled ? "translateX(16px)" : "translateX(0)", transition: "transform .16s" }} />
              </Box>
            </Box>

            {openButEmpty && (
              <Box sx={{ display: "flex", gap: 1, p: 1.25, borderRadius: "var(--r-md)", background: "rgba(252,193,68,0.1)", border: "1px solid rgba(252,193,68,0.3)" }}>
                <Box sx={{ color: "var(--amber)", flexShrink: 0, display: "flex" }}><OpusIcon name="alertTriangle" size={16} /></Box>
                <Typography sx={{ fontSize: 12.5, color: "var(--amber)" }}>Enabled but no rules added — still open to all. Add an IP/CIDR or country below to actually restrict.</Typography>
              </Box>
            )}

            {/* IP allowlist */}
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", mb: 0.75 }}>Allowed IPs / CIDR ranges</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField size="small" fullWidth placeholder="203.0.113.4 or 10.0.0.0/24" value={ipInput}
                  onChange={(e) => setIpInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIp(); } }}
                  disabled={!enabled} />
                <OpusButton variant="secondary" size="sm" icon="plus" onClick={addIp} disabled={!enabled}>Add</OpusButton>
              </Box>
              <Chips items={ips} onRemove={(v) => setIps(ips.filter((x) => x !== v))} />
            </Box>

            {/* Countries */}
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", mb: 0.75 }}>Allowed countries (ISO2)</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField size="small" fullWidth placeholder="GB, US, …" value={countryInput}
                  onChange={(e) => setCountryInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCountry(); } }}
                  disabled={!enabled} />
                <OpusButton variant="secondary" size="sm" icon="plus" onClick={addCountry} disabled={!enabled}>Add</OpusButton>
              </Box>
              <Chips items={countries} onRemove={(v) => setCountries(countries.filter((x) => x !== v))} />
            </Box>

            <Typography sx={{ fontSize: 12, color: "var(--text-dim)" }}>
              Empty lists = open. {type !== "org" ? "If this has no lock, the organisation's lock applies as a fallback; a lock here overrides it." : "This applies as the fallback default for everything in the organisation that has no lock of its own."}
              {updatedBy ? ` · Last set by ${updatedBy}.` : ""}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <OpusButton variant="ghost" size="sm" onClick={onClose}>Cancel</OpusButton>
        <OpusButton variant="success" size="sm" icon="check" onClick={save} disabled={saving || loading}>{saving ? "Saving…" : "Save lock"}</OpusButton>
      </DialogActions>
    </Dialog>
  );
}
